import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Container, Typography, Box, Button, Paper, CircularProgress,
  Alert, Pagination, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Skeleton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import BloodTypePicker, { type BloodType } from '../components/BloodTypePicker';
import CascadingDropdown from '../components/CascadingDropdown';
import DonorCard, { type Donor } from '../components/DonorCard';
import { getBrowserFingerprint } from '../lib/fingerprint';

const PAGE_SIZE = 10;

interface SearchResult {
  data: Donor[];
  meta: { total: number; page: number; limit: number };
}

export default function SearchPage() {
  const { t } = useTranslation();

  const [bloodType, setBloodType]     = useState<BloodType | null>(null);
  const [governorate, setGovernorate] = useState('');
  const [city, setCity]               = useState('');

  const [results, setResults]         = useState<SearchResult | null>(null);
  const [page, setPage]               = useState(1);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [limitReached, setLimitReached] = useState(false);

  // Generate browser fingerprint once on mount
  const fingerprintRef = useRef('');
  useEffect(() => {
    getBrowserFingerprint().then((fp) => { fingerprintRef.current = fp; });
  }, []);

  const handleLocation = useCallback((_c: string, gov: string, ct: string) => {
    setGovernorate(gov);
    setCity(ct);
  }, []);

  const doSearch = async (targetPage = 1) => {
    setLoading(true);
    setError('');
    setLimitReached(false);

    const params = new URLSearchParams();
    if (bloodType)   params.set('blood_type', bloodType);
    if (governorate) params.set('governorate', governorate);
    if (city)        params.set('city', city);
    params.set('page', String(targetPage));
    params.set('limit', String(PAGE_SIZE));

    try {
      const apiBase = import.meta.env.VITE_API_BASE_URL ?? '';
      const headers: Record<string, string> = {};
      if (fingerprintRef.current) headers['X-Client-Fingerprint'] = fingerprintRef.current;
      const res = await fetch(`${apiBase}/api/search?${params}`, { headers });
      const json = await res.json();

      if (res.status === 429) {
        setLimitReached(true);
        return;
      }
      if (!res.ok) throw new Error(json.error ?? t('common.error'));

      setResults(json as SearchResult);
      setPage(targetPage);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const totalPages = results ? Math.ceil(results.meta.total / PAGE_SIZE) : 0;

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h4" fontWeight={800} mb={4} textAlign="center">
        🩸 {t('search.title')}
      </Typography>

      {/* Filter panel */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <BloodTypePicker
            label={t('search.blood_type')}
            value={bloodType}
            onChange={setBloodType}
          />
          <CascadingDropdown onChange={handleLocation} />
          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
            onClick={() => doSearch(1)}
            disabled={loading}
          >
            {t('search.search_btn')}
          </Button>
        </Box>
      </Paper>

      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Loading skeletons */}
      {loading && (
        <Box>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={100} sx={{ mb: 2, borderRadius: 2 }} />
          ))}
        </Box>
      )}

      {/* Results */}
      {!loading && results && (
        <>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {results.meta.total} {results.meta.total === 1 ? 'donor' : 'donors'} found
          </Typography>

          {results.data.length === 0 ? (
            <Alert severity="info">{t('search.no_results')}</Alert>
          ) : (
            <>
              {results.data.map((donor) => (
                <DonorCard key={donor.id} donor={donor} />
              ))}

              {/* Pagination — 10 donors per page */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    color="primary"
                    shape="rounded"
                    onChange={(_, p) => doSearch(p)}
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* Rate limit dialog */}
      <Dialog open={limitReached} onClose={() => setLimitReached(false)}>
        <DialogTitle>🚫 {t('search.limit_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('search.limit_body')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitReached(false)} variant="contained" autoFocus>
            {t('search.limit_ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
